<script>
import Layout from "../../layouts/main";
import PageHeader from "../../components/page-header";
import Swal from '@/utils/ugm-swal';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default {
    components: { Layout, PageHeader },
    data() {
        return {
            title: "Pagos Laborantes",
            items: [
                {
                    text: "Laborantes",
                    href: "/"
                },
                {
                    text: "Pago",
                    active: true
                }
            ],
            laborantes_por_pagar: [],
            laboranteDetalle: {
                nombre_completo: '',
                pagos: []
            },
            currentPage: 1,
            perPage: 10,
            pageOptions: [10, 25, 50, 100],
            filtro: '',
            isLoading: false,
            isPaying: false,
            apagar: []
        };
    },
    computed: {
        puedePagar() {
            return !!this.user && ['SuperAdmin', 'Admin'].includes(this.user.rol);
        },
        rows() {
            return this.filteredData.length;
        },
        filteredData() {
            const filtro = (this.filtro || '').toLowerCase().trim();
            const datos = (this.laborantes_por_pagar || []).filter((item) => {
                if (!filtro) {
                    return true;
                }

                return [
                    item.rut,
                    item.nombre,
                    item.apellidos,
                    item.nombre_completo,
                    item.email,
                    item.banco,
                    item.tipo_cuenta,
                    item.numero_cuenta
                ].some((valor) => String(valor || '').toLowerCase().includes(filtro));
            });

            return [...datos].sort((a, b) => {
                return (Number(b.total_liquido) || 0) - (Number(a.total_liquido) || 0);
            });
        },
        paginatedData() {
            const start = (this.currentPage - 1) * this.perPage;
            return this.filteredData.slice(start, start + this.perPage);
        },
        totalLiquido() {
            return this.filteredData.reduce((total, item) => total + (Number(item.total_liquido) || 0), 0);
        },
        totalBruto() {
            return this.filteredData.reduce((total, item) => total + (Number(item.total_bruto) || 0), 0);
        },
        totalHoras() {
            return this.filteredData.reduce((total, item) => total + (Number(item.total_horas) || 0), 0);
        }
    },
    watch: {
        filtro() {
            this.currentPage = 1;
        },
        perPage() {
            this.currentPage = 1;
        }
    },
    methods: {
        cargarPagos() {
            this.isLoading = true;
            return axios.get('/api/get_laborantes_por_pagar_agrupados')
                .then(response => {
                    this.laborantes_por_pagar = response.data.laborantes || [];
                    const totalPages = Math.max(1, Math.ceil(this.filteredData.length / this.perPage));
                    if (this.currentPage > totalPages) {
                        this.currentPage = totalPages;
                    }
                })
                .catch(() => {
                    this.laborantes_por_pagar = [];
                    this.msg('No se pudieron cargar los pagos', 'error');
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        msg(title, icon = 'success') {
            Swal.fire({
                icon,
                title,
                showConfirmButton: false,
                timer: 1400
            });
        },
        verDetalle(laborante) {
            this.laboranteDetalle = laborante;
            this.$refs['modal-detalle'].show();
        },
        formatFecha(fecha) {
            if (!fecha) {
                return '-';
            }

            const partes = String(fecha).split('-');
            return partes.length === 3 ? `${partes[2]}-${partes[1]}-${partes[0]}` : fecha;
        },
        formatHoras(valor) {
            const horas = Number(String(valor || 0).replace(',', '.'));
            return Number.isFinite(horas)
                ? horas.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : '0,00';
        },
        formatValor(valor) {
            const monto = Number(String(valor || 0).replace(/[^0-9-]/g, '')) || 0;
            return monto.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' });
        },
        numeroExportacion(valor) {
            return Number(String(valor || 0).replace(/[^0-9-]/g, '')) || 0;
        },
        filaExportacion(item = {}) {
            return {
                RUT: item.rut || '',
                NOMBRE: item.nombre || '',
                APELLIDOS: item.apellidos || '',
                EMAIL: item.email || '',
                'MONTO LIQUIDO': this.numeroExportacion(item.total_liquido),
                'MONTO BRUTO': this.numeroExportacion(item.total_bruto),
                HORAS: Number(String(item.total_horas || 0).replace(',', '.')) || 0,
                BANCO: item.banco || '',
                'TIPO DE CUENTA': item.tipo_cuenta || '',
                'NUMERO DE CUENTA': item.numero_cuenta || '',
                'NUMERO BOLETA': ''
            };
        },
        periodoExportacion() {
            const fechas = this.laborantes_por_pagar
                .reduce((resultado, laborante) => {
                    return resultado.concat((laborante.pagos || []).map(pago => pago.fecha));
                }, [])
                .filter(Boolean)
                .sort();

            if (!fechas.length) {
                return '';
            }

            const formatoCorto = fecha => {
                const partes = String(fecha).split('-');
                return partes.length === 3 ? `${partes[2]}-${partes[1]}` : fecha;
            };

            return `PAGOS DEL ${formatoCorto(fechas[0])} AL ${formatoCorto(fechas[fechas.length - 1])}`;
        },
        pagar() {
            if (!this.puedePagar || this.isPaying) {
                return;
            }

            this.apagar = this.laborantes_por_pagar.reduce((pagos, laborante) => {
                return pagos.concat(laborante.pagos || []);
            }, []);

            if (!this.apagar.length) {
                this.msg('No hay pagos seleccionados', 'info');
                return;
            }

            Swal.fire({
                title: '¿Marcar pagos como pagados?',
                text: `Se procesarán ${this.apagar.length} pagos.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, pagar',
                cancelButtonText: 'Cancelar'
            }).then(result => {
                if (!result.isConfirmed) {
                    return;
                }

                this.isPaying = true;
                const formData = new FormData();
                formData.append('apagar', JSON.stringify(this.apagar.map(pago => ({ id: pago.id }))));

                axios.post('/api/pagar', formData)
                    .then(response => {
                        if (response.data.estado) {
                            this.msg('Laborantes pagados');
                            return this.cargarPagos();
                        }
                        this.msg('No se pudieron pagar los registros', 'error');
                    })
                    .catch(() => {
                        this.msg('No se pudieron pagar los registros', 'error');
                    })
                    .finally(() => {
                        this.isPaying = false;
                    });
            });
        },
        exportToExcelCompleto() {
            const filas = this.laborantes_por_pagar.map(laborante => this.filaExportacion(laborante));
            const total = this.filaExportacion();
            total.EMAIL = 'MONTO TOTAL';
            total['MONTO LIQUIDO'] = this.laborantes_por_pagar.reduce((suma, laborante) => {
                return suma + this.numeroExportacion(laborante.total_liquido);
            }, 0);
            total['MONTO BRUTO'] = this.laborantes_por_pagar.reduce((suma, laborante) => {
                return suma + this.numeroExportacion(laborante.total_bruto);
            }, 0);
            total.HORAS = this.laborantes_por_pagar.reduce((suma, laborante) => {
                return suma + (Number(String(laborante.total_horas || 0).replace(',', '.')) || 0);
            }, 0);

            const periodo = this.filaExportacion();
            periodo.EMAIL = this.periodoExportacion();
            filas.push(total, periodo);

            const ws = XLSX.utils.json_to_sheet(filas);
            ws['!cols'] = [
                { wch: 16 },
                { wch: 24 },
                { wch: 26 },
                { wch: 40 },
                { wch: 18 },
                { wch: 18 },
                { wch: 12 },
                { wch: 22 },
                { wch: 22 },
                { wch: 22 },
                { wch: 18 }
            ];
            const rango = XLSX.utils.decode_range(ws['!ref']);
            for (let columna = 4; columna <= 5; columna += 1) {
                for (let fila = 1; fila <= rango.e.r; fila += 1) {
                    const celda = ws[XLSX.utils.encode_cell({ r: fila, c: columna })];
                    if (celda) {
                        celda.z = '#,##0';
                    }
                }
            }
            for (let fila = 1; fila <= rango.e.r; fila += 1) {
                const celda = ws[XLSX.utils.encode_cell({ r: fila, c: 6 })];
                if (celda) {
                    celda.z = '0.00';
                }
            }

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Datos");
            const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "datos.xlsx");
        },
        exportToExcelBancarios() {
            axios.get('/api/export_datos_bancarios').then(response => {
                const ws = XLSX.utils.json_to_sheet(response.data.array);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Datos");
                const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
                saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "DatosBancarios.xlsx");
            });
        }
    },
    created() {
        this.cargarPagos();
    }
};
</script>
<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <div class="row">
            <div class="col-md-12">
                <div class="card">
                    <div class="card-header py-3 d-flex align-items-center"
                        style="background: linear-gradient(135deg, #252C5F 0%, #61B7E4 100%); border-radius: 4px 4px 0 0;">
                        <i class="bx bx-wallet-alt-2 font-size-24 text-white mr-2"></i>
                        <div>
                            <h5 class="text-white mb-0 font-weight-bold">Por Pagar</h5>
                            <small class="text-white" style="opacity:.8;">Resumen por laborante de los pagos seleccionados</small>
                        </div>
                    </div>

                    <div class="card-body text-center" v-if="isLoading">
                        <b-spinner variant="primary" class="mb-2"></b-spinner>
                        <p class="text-muted mb-0">Cargando pagos...</p>
                    </div>

                    <div class="card-body" v-else-if="laborantes_por_pagar.length > 0">
                        <div class="row align-items-center mb-3">
                            <div class="col-md-8 mb-2 mb-md-0">
                                <b-button variant="primary" class="mr-2" @click="exportToExcelCompleto">
                                    Exportar excel completo
                                </b-button>
                                <b-button variant="primary" @click="exportToExcelBancarios">
                                    Exportar datos bancarios
                                </b-button>
                            </div>
                            <div class="col-md-4 text-md-right">
                                <b-button v-if="puedePagar" variant="success" :disabled="isPaying" @click="pagar">
                                    <b-spinner v-if="isPaying" small class="mr-1"></b-spinner>
                                    <i v-else class="bx bx-money mr-1"></i>
                                    {{ isPaying ? 'Procesando...' : 'Pagar seleccionados' }}
                                </b-button>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-md-4 mb-2 mb-md-0">
                                <div class="border rounded p-2 h-100">
                                    <small class="text-muted d-block">Laborantes</small>
                                    <strong>{{ filteredData.length }}</strong>
                                </div>
                            </div>
                            <div class="col-md-4 mb-2 mb-md-0">
                                <div class="border rounded p-2 h-100">
                                    <small class="text-muted d-block">Total líquido</small>
                                    <strong>{{ formatValor(totalLiquido) }}</strong>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="border rounded p-2 h-100">
                                    <small class="text-muted d-block">Total bruto</small>
                                    <strong>{{ formatValor(totalBruto) }}</strong>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-sm-12 col-md-6">
                                <label class="d-inline-flex align-items-center">
                                    Mostrar&nbsp;
                                    <b-form-select v-model="perPage" size="sm" :options="pageOptions"></b-form-select>&nbsp;laborantes
                                </label>
                            </div>
                            <div class="col-sm-12 col-md-6 text-md-right">
                                <label class="d-inline-flex align-items-center">
                                    Buscar:
                                    <b-form-input
                                        v-model="filtro"
                                        type="search"
                                        placeholder="RUT, nombre o correo..."
                                        class="form-control form-control-sm ml-2"
                                    ></b-form-input>
                                </label>
                            </div>
                        </div>

                        <div class="table-responsive mb-0 mt-3">
                            <table class="table table-hover table-sm">
                                <thead>
                                    <tr>
                                        <th>RUT</th>
                                        <th>NOMBRE</th>
                                        <th>APELLIDOS</th>
                                        <th>EMAIL</th>
                                        <th class="text-right">MONTO LÍQUIDO</th>
                                        <th class="text-right">MONTO BRUTO</th>
                                        <th class="text-right">HORAS</th>
                                        <th>BANCO</th>
                                        <th>TIPO DE CUENTA</th>
                                        <th>NÚMERO DE CUENTA</th>
                                        <th class="text-center">DETALLE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in paginatedData" :key="item.laborante_id">
                                        <td class="text-nowrap">{{ item.rut }}</td>
                                        <td>{{ item.nombre || '-' }}</td>
                                        <td>{{ item.apellidos || '-' }}</td>
                                        <td>{{ item.email || '-' }}</td>
                                        <td class="text-right text-nowrap">{{ formatValor(item.total_liquido) }}</td>
                                        <td class="text-right text-nowrap">{{ formatValor(item.total_bruto) }}</td>
                                        <td class="text-right text-nowrap">{{ formatHoras(item.total_horas) }}</td>
                                        <td>{{ item.banco || '-' }}</td>
                                        <td>{{ item.tipo_cuenta || '-' }}</td>
                                        <td class="text-nowrap">{{ item.numero_cuenta || '-' }}</td>
                                        <td class="text-center">
                                            <b-button
                                                variant="link"
                                                class="p-0 text-primary"
                                                :title="`Ver desglose de ${item.nombre_completo}`"
                                                :aria-label="`Ver desglose de ${item.nombre_completo}`"
                                                @click="verDetalle(item)"
                                            >
                                                <i class="bx bx-show font-size-20"></i>
                                            </b-button>
                                        </td>
                                    </tr>
                                    <tr v-if="!paginatedData.length">
                                        <td colspan="11" class="text-center text-muted">No hay resultados para la búsqueda.</td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr class="font-weight-bold bg-light">
                                        <td colspan="4">TOTAL</td>
                                        <td class="text-right text-nowrap">{{ formatValor(totalLiquido) }}</td>
                                        <td class="text-right text-nowrap">{{ formatValor(totalBruto) }}</td>
                                        <td class="text-right text-nowrap">{{ formatHoras(totalHoras) }}</td>
                                        <td colspan="4"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div class="row">
                            <div class="col">
                                <div class="dataTables_paginate paging_simple_numbers float-right">
                                    <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage"></b-pagination>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card-body text-center" v-else>
                        <h4 class="text-primary">No hay laborantes seleccionados para pagar</h4>
                        <b-button variant="primary" href="/web/laborantes/pagos">Revisar listado</b-button>
                    </div>
                </div>
            </div>
        </div>

        <b-modal ref="modal-detalle" size="xl" hide-footer :title="'Desglose de pagos - ' + (laboranteDetalle.nombre_completo || '')">
            <div class="mb-3 text-muted">
                <span class="mr-3"><strong>RUT:</strong> {{ laboranteDetalle.rut || '-' }}</span>
                <span><strong>Email:</strong> {{ laboranteDetalle.email || '-' }}</span>
            </div>
            <div class="table-responsive mb-0">
                <table class="table table-hover table-sm">
                    <thead>
                        <tr>
                            <th>Nº</th>
                            <th>EVENTO(S)</th>
                            <th>ACTIVIDAD</th>
                            <th>FECHA</th>
                            <th>HORARIO</th>
                            <th>TIPO</th>
                            <th>SUBTIPO</th>
                            <th>TIPO LABORANTE</th>
                            <th class="text-right">HORAS</th>
                            <th>COORDINADOR</th>
                            <th>COMENTARIO</th>
                            <th class="text-right">BRUTO</th>
                            <th class="text-right">LÍQUIDO</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(pago, index) in (laboranteDetalle.pagos || [])" :key="pago.id">
                            <td>{{ index + 1 }}</td>
                            <td>{{ pago.cod_evento || '-' }}</td>
                            <td>{{ pago.nombre_evento || '-' }}</td>
                            <td class="text-nowrap">{{ formatFecha(pago.fecha) }}</td>
                            <td class="text-nowrap">{{ pago.hora_inicio || '-' }} - {{ pago.hora_fin || '-' }}</td>
                            <td>{{ pago.tipo_nombre || '-' }}</td>
                            <td>{{ pago.subtipo_nombre || '-' }}</td>
                            <td>{{ pago.tipo_laborante || '-' }}</td>
                            <td class="text-right">{{ formatHoras(pago.total_horas) }}</td>
                            <td>{{ pago.coordinador || '-' }}</td>
                            <td>{{ pago.comentario || '-' }}</td>
                            <td class="text-right text-nowrap">{{ formatValor(pago.valor_bruto) }}</td>
                            <td class="text-right text-nowrap">{{ formatValor(pago.valor_liquido) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </b-modal>
    </Layout>
</template>